/**
 * "Log a contact" — the first HSP child-row write FORM.
 *
 * It sets the pattern for rates, loads and blackouts when #29 lands, so the
 * decisions here are deliberately conservative:
 *
 *  1. CAPABILITY-GATED, and it says why rather than vanishing. `viewer` holds
 *     `view:supplier_tab` but not `write:supplier`, and a section that silently
 *     lacks a button leaves someone hunting for a feature they cannot have. The
 *     server gate is the real one — this only avoids offering an action that would
 *     403.
 *  2. THE SERVER OWNS `contactedBy`. There is no "who" field, because the answer is
 *     the signed-in user and offering it would let it be wrong. Item 19 asked for
 *     "contacted by" as something to SEE, not to type.
 *  3. THE SUBMIT IS IDEMPOTENT-ISH BY DISABLING, not by a token. A double-click
 *     would otherwise log the same call twice, and two identical rows in an
 *     evidence log are worse than none — you cannot tell which is the real one.
 *     A proper idempotency key belongs on the whole HSP write surface rather than
 *     being invented here for one form.
 *  5. IT OPENS IN A MODAL, not by growing the panel. Stacie, 2026-09-03: "maybe
 *     when we click log a contact, it pops out so that it's not so overwhelming
 *     to keep scrolling within the same thing for a mile." The inline form
 *     pushed the contact history — the thing you consult WHILE writing the log —
 *     off the bottom of a panel that is already long. Jeff: "log a contact could
 *     open a modal… I think that's way better." Her: "I agree."
 *
 *     Escape and the backdrop ask first when anything has been typed. Someone
 *     writing up a call has the only copy of it in that textarea.
 *
 *  4. IT REFETCHES rather than optimistically inserting. The list is short, the
 *     write is rare, and an optimistic row that later fails to persist is a lie
 *     that survives until reload. The parent's `onSaved` triggers the refetch so
 *     the derived last-contact summary is recomputed by the server, not guessed at
 *     here — otherwise the summary and the list could disagree.
 */
import { useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { Modal } from '@/features/crm/components/Modal';
import {
  CONTACT_REASONS,
  REASONS_WANTING_AN_AGREEMENT,
} from '../contact-reasons';
import { statusLabel } from '../agreement-status';

/**
 * Mirrors CONTACT_METHODS in customer-management-api/src/lib/hsp/supplier-contact.
 * Duplicated rather than shared because the two apps have no shared package for
 * this yet; the API rejects anything it does not know, so the failure mode of
 * drift is a 400 with the valid list in it, not a bad write.
 */
const METHODS: { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'portal', label: 'Portal / extranet' },
  { value: 'in_person', label: 'In person' },
  { value: 'other', label: 'Other' },
];

const DIRECTIONS: { value: string; label: string }[] = [
  { value: 'outbound', label: 'We contacted them' },
  { value: 'inbound', label: 'They contacted us' },
];

/** One agreement the caller may link this contact to. */
export interface LinkableAgreement {
  id: string;
  status: string;
  customerId: string;
  customerName: string | null;
}

/** One person at the property who may have been spoken to. */
export interface SelectableContact {
  id: string;
  name: string;
  role: string | null;
  status?: string;
}

/**
 * `datetime-local` wants `YYYY-MM-DDTHH:mm` in LOCAL time, with no zone.
 *
 * Built from the local getters rather than `toISOString().slice(0,16)`, which
 * yields UTC and would pre-fill the control with a time the user did not have —
 * out by the offset, six hours wrong in Denver, and silently so.
 */
function localDateTimeValue(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return [
    now.getFullYear(),
    '-',
    pad(now.getMonth() + 1),
    '-',
    pad(now.getDate()),
    'T',
    pad(now.getHours()),
    ':',
    pad(now.getMinutes()),
  ].join('');
}

/**
 * The `contacted_at` half of the request body — `{}` when the control is empty or
 * holds something unparseable.
 *
 * `datetime-local` is clearable and a partially-typed value reads back as `''`, so
 * `new Date(value).toISOString()` can THROW RangeError mid-submit. Returning a
 * spreadable object rather than a string keeps that decision here: an absent
 * `contacted_at` is a documented default (the server uses now, which is what a
 * blank "when" means), whereas a thrown RangeError would surface to the user as
 * "Invalid time value" over a form that looks perfectly filled in.
 */
function isoInstantOf(localValue: string): { contacted_at?: string } {
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? {} : { contacted_at: d.toISOString() };
}

export interface LogContactFormProps {
  /** What the trigger shows. Defaults to a "Log a contact" button label. */
  triggerLabel?: React.ReactNode;
  /** Trigger classes. `btn` by default; a cell passes something cell-shaped. */
  triggerClassName?: string;
  /** Tooltip for a trigger whose label alone does not say what it does. */
  triggerTitle?: string;
  /**
   * At least one — and both is now normal, matching the API after migration
   * 20260903190000. An agreement-level log inherits the property server-side,
   * so a caller with only `agreementId` still gets the read-across.
   */
  agreementId?: string;
  hotelCanonicalId?: string;
  /**
   * The agreements at this property, for the reason field's picker.
   *
   * PASSED IN, not fetched. The hotel panel already has them —
   * `/api/btp/hotels` returns each property's agreement rollup — so a request
   * here would re-ask a question the parent has already answered. Same split
   * `AgreementRollup` documents: eager when the parent already carries it,
   * self-fetching only when unbounded per parent.
   *
   * Absent means "no picker", which is right on the AGREEMENT panel: the
   * agreement is the parent there, so asking which one would be asking the user
   * to restate where they are.
   */
  agreements?: LinkableAgreement[];
  /**
   * The people at this property, for "who did you speak to".
   *
   * Passed in so this component stays a pure form — `SupplierContactLog` owns
   * the request. Keeping the fetch out of here is what lets the spec drive the
   * picker from a literal instead of mocking a second endpoint.
   */
  contacts?: SelectableContact[];
  /** Called after a successful write so the parent refetches the log. */
  onSaved: () => void;
  /**
   * Called when the modal OPENS, so the parent can refresh what the pickers
   * offer.
   *
   * This exists because of a bug found by driving the browser: the contacts
   * list was fetched once on mount, so retiring someone in the sibling
   * `HotelContacts` table left them still selectable here until the panel
   * remounted — and logging a call against a person who has left the property
   * is exactly the wrong record the retire rule exists to prevent.
   *
   * Opening is the right moment to ask, not mount: the list is only needed
   * while the form is open, and it is then as fresh as it can be.
   */
  onOpen?: () => void;
  testId?: string;
}

export function LogContactForm({
  triggerLabel,
  triggerClassName = 'btn',
  triggerTitle,
  agreementId,
  hotelCanonicalId,
  agreements,
  contacts,
  onSaved,
  onOpen,
  testId = 'log-contact',
}: LogContactFormProps) {
  const { can } = useMe();
  const canWrite = can('write:supplier');

  const [open, setOpen] = useState(false);
  /*
   * METHODS, PLURAL AND ORDERED. Stacie, 2026-09-03: "A lot of times I will have
   * my team call them. If they don't get a hold of them, then they are
   * immediately supposed to email."
   *
   * An ARRAY, not a Set: the order is information she stated — called first,
   * then emailed — and the API preserves it. A Set would silently normalise to
   * insertion order anyway while making that dependence invisible.
   *
   * Multi-select rather than a combined vocabulary, which was Jeff's call:
   * "instead of having to create a permutation of all of the combinations."
   */
  const [methods, setMethods] = useState<string[]>(['email']);
  const [direction, setDirection] = useState('outbound');
  const [contactedAt, setContactedAt] = useState(() => localDateTimeValue());
  const [reason, setReason] = useState('general');
  /** The agreement this contact is about, when the reason names one. */
  const [linkedAgreementId, setLinkedAgreementId] = useState('');
  /** Which people at the property were spoken to. Empty is normal. */
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * The picker only appears for a reason that names an agreement, and only
   * where there is a choice to make — so not on the agreement panel, where the
   * agreement is the parent.
   */
  const linkable = agreements ?? [];
  const wantsAgreement =
    !agreementId &&
    linkable.length > 0 &&
    REASONS_WANTING_AN_AGREEMENT.has(reason);

  /*
   * RETIRED CONTACTS ARE NOT OFFERED, but are not forgotten either.
   *
   * Someone who has left the property cannot have been in today's call, so
   * listing them invites a wrong record. They still resolve in the HISTORY —
   * that is the whole reason Remove retires rather than deletes — so this is
   * about what can be newly asserted, not about what happened.
   */
  const selectable = (contacts ?? []).filter(
    (c) => c.status !== 'left_property',
  );

  /*
   * THERE IS NO EFFECT CLEARING `linkedAgreementId` WHEN THE REASON CHANGES,
   * and there was one until a mutation test proved it dead.
   *
   * It existed to stop the form submitting an agreement the user could no
   * longer see they had chosen. But the submit body already gates on
   * `wantsAgreement && linkedAgreementId`, so deleting the effect changed no
   * observable behaviour — the test written for it stayed green. A
   * state-syncing effect whose absence nothing can detect is a second place for
   * the same rule to live, and drift.
   *
   * Weakening the REAL guard to `linkedAgreementId` alone does fail that test,
   * which is how the load-bearing mechanism was identified.
   *
   * The one behaviour the effect did have: toggling the reason away and back
   * now keeps the previously chosen agreement, which is better anyway.
   */

  if (!agreementId && !hotelCanonicalId) return null;

  // Says why rather than rendering nothing. A missing button reads as a bug or as
  // a feature you have not found; this reads as a permission.
  if (!canWrite) {
    return (
      <p
        data-testid={`${testId}-readonly`}
        style={{
          fontSize: 11,
          color: 'var(--text-secondary)',
          margin: '8px 0 0',
        }}
      >
        You have read-only access to supplier records, so logging a contact
        isn&rsquo;t available on this account.
      </p>
    );
  }

  const reset = () => {
    setMethods(['email']);
    setDirection('outbound');
    setContactedAt(localDateTimeValue());
    setReason('general');
    setLinkedAgreementId('');
    setParticipantIds([]);
    setNotes('');
    setError(null);
  };

  /** Toggle one method, keeping the order they were chosen in. */
  const toggleMethod = (value: string) =>
    setMethods((current) =>
      current.includes(value)
        ? current.filter((m) => m !== value)
        : [...current, value],
    );

  const toggleParticipant = (id: string) =>
    setParticipantIds((current) =>
      current.includes(id) ? current.filter((p) => p !== id) : [...current, id],
    );

  /*
   * "Dirty" is deliberately NARROW: notes typed, or a method/direction moved off
   * its default. The `when` field is pre-filled with now and nudging it is not
   * work worth guarding — asking about it would train people to dismiss the
   * prompt, which is how a guard stops working.
   */
  const dirty =
    notes.trim() !== '' ||
    methods.length !== 1 ||
    methods[0] !== 'email' ||
    direction !== 'outbound' ||
    reason !== 'general' ||
    participantIds.length > 0;

  const close = () => {
    reset();
    setOpen(false);
  };

  /** Guards the ACCIDENTAL dismissals only — Escape and the backdrop. */
  const confirmDismiss = () =>
    !dirty ||
    window.confirm(
      'Discard this contact log?\n\nWhat you have typed is not saved anywhere else.',
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard the handler as well as the button. A form submits on Enter from any
    // field, which does not go through the disabled button at all.
    if (saving) return;
    // Enter submits from any field and never touches the disabled button, so
    // the empty-methods guard has to live here as well — same reason the
    // double-submit guard does.
    if (methods.length === 0) {
      setError('Pick at least one method.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiFetch('/api/btp/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          /*
           * BOTH PARENTS WHEN WE HAVE BOTH, which the database allows as of
           * migration 20260903190000 and used to forbid.
           *
           * On the hotel panel with a reason that names an agreement, this is
           * the row Stacie's example needs: it belongs to that agreement AND
           * shows in the property's own history, so the next person to ring
           * the hotel sees it whichever way they came in.
           *
           * An agreement-level log sends only the agreement and lets the route
           * inherit the property — the server knows the mapping and the browser
           * would only be guessing at it.
           */
          ...(agreementId ? { agreement_id: agreementId } : {}),
          ...(hotelCanonicalId ? { hotel_canonical_id: hotelCanonicalId } : {}),
          ...(wantsAgreement && linkedAgreementId
            ? { agreement_id: linkedAgreementId }
            : {}),
          methods,
          reason,
          ...(participantIds.length > 0 ? { contact_ids: participantIds } : {}),
          direction,
          // `datetime-local` gives a zoneless local string. `new Date(...)` reads
          // it as LOCAL, which is what the user typed, and toISOString then sends
          // the correct instant — with an explicit offset, which the API requires
          // so it never has to guess a timezone. Sending the raw string would have
          // the server read it as UTC.
          //
          // Spread rather than always sent: the field is clearable, and
          // `new Date('').toISOString()` THROWS a RangeError, which the catch below
          // would surface as "Invalid time value" over a form that looks fine.
          // Omitting the key instead lets the server apply its documented default
          // of now — which is what an empty "when" means anyway.
          ...isoInstantOf(contactedAt),
          notes: notes.trim() || null,
        }),
      });
      close();
      // Refetch rather than optimistically insert: the server recomputes the
      // derived last-contact summary, so the list and the summary cannot disagree.
      onSaved();
    } catch (err) {
      // The API's message is shown verbatim — it names the field and the valid
      // values, which is more useful than a generic "could not save".
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // THE TRIGGER IS ALWAYS RENDERED, and the parent decides where. It sits ABOVE
  // the contact history — "that's my main ask", Stacie said it twice — which is
  // the parent's job, not this component's.
  /*
   * THE TRIGGER IS THE CALLER'S, because one of the callers is a table cell.
   *
   * Jeff, 2026-09-09, after the status chip became its own control: "Can we do
   * something similar with the last 'contacted date' column? That'd make it WAY
   * easier for the team to move down the list to make their contacts." So the
   * Last-contacted-date cell renders THIS component with the date itself as the
   * trigger — the value you are about to change is the thing you click, same as
   * the status chip.
   *
   * A `.btn` labelled "Log a contact" is still the default, because the panel
   * and the drawer both want exactly that.
   */
  const trigger = (
    <button
      className={triggerClassName}
      data-testid={`${testId}-open`}
      onClick={() => {
        onOpen?.();
        setOpen(true);
      }}
      title={triggerTitle}
      type="button"
    >
      {triggerLabel ?? 'Log a contact'}
    </button>
  );

  if (!open) return <div>{trigger}</div>;

  return (
    <div>
      {trigger}
      <Modal
        confirmDismiss={confirmDismiss}
        onClose={close}
        testId={`${testId}-modal`}
        title="Log a contact"
      >
        <form data-testid={`${testId}-form`} onSubmit={submit}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 10,
            }}
          >
            <div>
              <label className="field-label" htmlFor={`${testId}-direction`}>
                Direction
              </label>
              <select
                className="input"
                data-testid={`${testId}-direction`}
                id={`${testId}-direction`}
                onChange={(e) => setDirection(e.target.value)}
                value={direction}
              >
                {DIRECTIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>

            {/*
             * METHOD IS A MULTI-SELECT, and checkboxes rather than a
             * multiple-select listbox: a native <select multiple> needs a
             * modifier key to pick a second value, which is a discoverability
             * problem for the exact case this exists for.
             *
             * A fieldset with a legend, not a label — one label cannot name a
             * group of inputs accessibly, and a screen reader reading "Method"
             * five times is worse than reading it once as a group.
             */}
            <fieldset
              data-testid={`${testId}-methods`}
              style={{
                border: 'none',
                margin: 0,
                padding: 0,
                gridColumn: '1 / -1',
              }}
            >
              <legend className="field-label" style={{ padding: 0 }}>
                Method <span style={{ fontWeight: 400 }}>(all that apply)</span>
              </legend>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 14px',
                  marginTop: 4,
                }}
              >
                {METHODS.map((m) => (
                  <label
                    key={m.value}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      checked={methods.includes(m.value)}
                      data-testid={`${testId}-method-${m.value}`}
                      onChange={() => toggleMethod(m.value)}
                      type="checkbox"
                    />
                    {m.label}
                  </label>
                ))}
              </div>
              {/* Says so rather than silently disabling submit: an empty
                  selection is the one state the API rejects, and the reason it
                  does is not guessable from a greyed-out button. */}
              {methods.length === 0 && (
                <p
                  data-testid={`${testId}-methods-required`}
                  style={{
                    fontSize: 11,
                    margin: '6px 0 0',
                    color: 'var(--text-primary)',
                  }}
                >
                  Pick at least one method — a contact with no method
                  can&rsquo;t be read back later.
                </p>
              )}
            </fieldset>

            <div>
              <label className="field-label" htmlFor={`${testId}-when`}>
                When
              </label>
              <input
                className="input"
                data-testid={`${testId}-when`}
                id={`${testId}-when`}
                // Capped at now: the API refuses a future contact, and a form that
                // offers a value the server rejects wastes the round trip.
                max={localDateTimeValue()}
                onChange={(e) => setContactedAt(e.target.value)}
                type="datetime-local"
                value={contactedAt}
              />
            </div>
          </div>

          {/*
           * WHY, and WHICH AGREEMENT — the pair Jeff described: "For the reasons,
           * I think you can use the different statuses that we have now. If the
           * status refers to an agreement that's post-sourcing, it would be nice
           * to add a drop down to select the specific agreement (if adding a
           * contact from the hotel section)."
           *
           * The picker appears CONDITIONALLY, which is the interesting part. A
           * reason of "general" is Stacie's vague hotel-level case — "discuss
           * partnership opportunities" — and asking which agreement that was
           * about would be asking a question with no answer.
           */}
          <div style={{ marginTop: 10 }}>
            <label className="field-label" htmlFor={`${testId}-reason`}>
              What was it about?
            </label>
            <select
              className="input"
              data-testid={`${testId}-reason`}
              id={`${testId}-reason`}
              onChange={(e) => setReason(e.target.value)}
              value={reason}
            >
              {CONTACT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {wantsAgreement && (
            <div style={{ marginTop: 10 }}>
              <label className="field-label" htmlFor={`${testId}-agreement`}>
                Which agreement?{' '}
                <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <select
                className="input"
                data-testid={`${testId}-agreement`}
                id={`${testId}-agreement`}
                onChange={(e) => setLinkedAgreementId(e.target.value)}
                value={linkedAgreementId}
              >
                {/* OPTIONAL, and the empty option says what happens rather than
                    being blank. Someone who rang about loading but cannot
                    remember which client's rate must still be able to save the
                    call — a required picker here would lose the record. */}
                <option value="">
                  Not sure — log it against the hotel only
                </option>
                {linkable.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.customerName ?? a.customerId} — {statusLabel(a.status)}
                  </option>
                ))}
              </select>
              <p
                className="detail-note"
                style={{ fontSize: 11, margin: '6px 0 0' }}
              >
                <span className="muted">
                  Linking it puts this contact on that agreement&rsquo;s history
                  as well as the hotel&rsquo;s.
                </span>
              </p>
            </div>
          )}

          {/*
           * WHO YOU SPOKE TO. "Normally when I'm meeting in person with a hotel,
           * I'm meeting all the people. I'm not just meeting the sales
           * director." So more than one, and checkboxes for the same reason the
           * methods are.
           *
           * Rendered only when the property's contacts are KNOWN — on the
           * agreement panel the caller passes them too, but a property with no
           * recorded contacts gets no empty box, because there is nothing to
           * pick and the answer is to add a point of contact first.
           */}
          {selectable.length > 0 && (
            <fieldset
              data-testid={`${testId}-participants`}
              style={{ border: 'none', margin: '10px 0 0', padding: 0 }}
            >
              <legend className="field-label" style={{ padding: 0 }}>
                Who did you speak to?{' '}
                <span style={{ fontWeight: 400 }}>(optional)</span>
              </legend>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginTop: 4,
                }}
              >
                {selectable.map((c) => (
                  <label
                    key={c.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                    }}
                  >
                    <input
                      checked={participantIds.includes(c.id)}
                      data-testid={`${testId}-participant-${c.id}`}
                      onChange={() => toggleParticipant(c.id)}
                      type="checkbox"
                    />
                    {c.name}
                    {c.role && (
                      <span className="muted" style={{ fontSize: 11 }}>
                        {c.role}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <div style={{ marginTop: 10 }}>
            <label className="field-label" htmlFor={`${testId}-notes`}>
              Notes <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              className="input"
              data-testid={`${testId}-notes`}
              id={`${testId}-notes`}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was said, what to do next…"
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
              value={notes}
            />
          </div>

          {error && (
            <p
              data-testid={`${testId}-error`}
              style={{
                fontSize: 12,
                margin: '10px 0 0',
                color: 'var(--text-primary)',
              }}
            >
              Couldn&rsquo;t save this contact — {error}
            </p>
          )}

          <div className="dialog-actions" style={{ marginTop: 12 }}>
            {/* CANCEL DOES NOT ASK, and that is the point of the split.
                Clicking a button labelled Cancel is a decision; Escape and a
                backdrop click are usually accidents. Prompting on the
                deliberate path is how people learn to dismiss the prompt
                without reading it, which is what breaks the guard on the
                accidental one. */}
            <button
              className="btn"
              data-testid={`${testId}-cancel`}
              disabled={saving}
              onClick={close}
              type="button"
            >
              Cancel
            </button>
            {/* Inside the <form>, so Enter from any field submits — the same
                path the disabled-guard in `submit` protects. */}
            <button
              className="btn btn-primary"
              data-testid={`${testId}-submit`}
              // Also disabled with no method, alongside the sentence above the
              // button explaining why — the API rejects an empty `methods`, and
              // a round trip to learn that is a round trip wasted.
              disabled={saving || methods.length === 0}
              type="submit"
            >
              {saving ? 'Saving…' : 'Save contact'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
